import React, { useState, useEffect, useRef, useCallback } from 'react';
import EventForm from './EventForm';
import Modal from './Modal';
import { api } from '../../functions/api';
import SideNavigationBar from '../reusables/SideNavigationBar/SideNavigationBar';
import { Delete, DeleteOutline } from '@mui/icons-material';
import Service from '../../services/httpService';
import CCService from '../../services/httpServiceWithoutSuperAdmin';
const PAGE_SIZE = 10
export default function Events() {
    const [eventTypes, setEventTypes] = useState([]);
    const [events, setEvents] = useState([]);

    // Pagination & Filter State
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [filter, setFilter] = useState('upcoming');

    // Loading & Error
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [editing, setEditing] = useState(null);
    const service = new CCService();
    const observer = useRef();

    // Fetch event types once
    useEffect(() => {
        api.fetchEventTypes()
            .then(data => setEventTypes(data.events))
            .catch(err => console.error(err));
    }, []);

    // Fetch events on mount & whenever filter changes
    useEffect(() => {
        setLoading(true);
        setError('');
        setPage(1);
        setHasMore(true);

        api.fetchEvents(1, undefined, filter)
            .then(data => {
                setEvents(data.events);
                if (data.events.length < PAGE_SIZE) setHasMore(false);
                setPage(2);
            })
            .catch(err => {
                console.error(err);
                setError('Failed to load events');
            })
            .finally(() => setLoading(false));
    }, [filter]);

    // Load more events
    const loadMore = useCallback(() => {
        if (loading || !hasMore) return;
        setLoading(true);

        api.fetchEvents(page, undefined, filter)
            .then(data => {
                setEvents(prev => [...prev, ...data.events]);
                if (data.events.length < PAGE_SIZE) setHasMore(false);
                setPage(prev => prev + 1);
            })
            .catch(err => console.error(err))
            .finally(() => setLoading(false));
    }, [page, filter, loading, hasMore]);

    // Intersection Observer
    const lastRef = useCallback(
        node => {
            if (loading) return;
            if (observer.current) observer.current.disconnect();

            observer.current = new IntersectionObserver(
                entries => {
                    if (entries[0].isIntersecting && hasMore) {
                        loadMore();
                    }
                },
                { root: null, rootMargin: '0px', threshold: 1.0 }
            );

            if (node) observer.current.observe(node);
        },
        [loading, hasMore, loadMore]
    );

    // Clear banner flags
    const clearBannerFlags = async currentId => {
        const others = events.filter(e => e.is_banner && e.id !== currentId);
        if (!others.length) return;

        await Promise.all(
            others.map(e => api.updateEvent(e.id, { is_banner: 0 }))
        );

        setEvents(prev =>
            prev.map(e =>
                others.some(o => o.id === e.id) ? { ...e, is_banner: 0 } : e
            )
        );
    };

    const handleReleaseCertificate = async (event) => {

        try {


            // const res = await service.post("http://192.168.1.116:5020/api/user/event/eventFeedback", {
            //     "event_id": 26,
            //     "blueprint_id": 10
            // });

            // console.log("res", res);

            const res = await service.post("/user/event/releaseCertificate", {
                title: event.title,
                date: event.date,
                event_id: event.id,
            });

            setEvents(prev => prev.map(e => e.id === event.id ? { ...e, is_certificate_required: "2" } : e));


        } catch (err) {
            console.error(err);
            alert('Could not release certificate');
        }
    };

    // Add event
    const handleAdd = async data => {
        try {
            if (data.is_banner) await clearBannerFlags(null);
            const res = await api.createEvent(data);
            setEvents(prev => [res.event, ...prev]);
            setEditing(null);
        } catch (err) {
            console.error(err);
            alert('Could not create event');
        }
    };

    // Update event
    const handleUpdate = async data => {
        try {
            if (data.is_banner) await clearBannerFlags(editing);
            await api.updateEvent(editing, data);
            setEvents(prev =>
                prev.map(e => (e.id === editing ? { ...e, ...data } : e))
            );
            setEditing(null);
        } catch (err) {
            console.error(err);
            alert('Could not update event');
        }
    };

    // Delete event
    const handleDelete = async id => {
        // if (!window.confirm('Delete this event?')) return;
        try {
            await api.deleteEvent(id);
            setEvents(prev => prev.filter(e => e.id !== id));
        } catch (err) {
            console.error(err);
            alert('Could not delete event');
        }
    };

    const categories = ['all', 'upcoming', 'past'];

    // console.log("events", events);

    return (
        <div style={{ background: '#f7f8fa', minHeight: '100vh', padding: 24, paddingLeft: "8rem" }}>

            <SideNavigationBar />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h1 style={{ margin: 0, fontSize: 28, color: '#333' }}>Event Administration</h1>
                <button style={{ background: '#1c8ea8', color: '#fff', padding: '10px 20px', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 16 }} onClick={() => setEditing(0)}>
                    + New Event
                </button>
            </div>

            <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
                {categories.map(cat => (
                    <button
                        key={cat}
                        onClick={() => setFilter(cat)}
                        style={{
                            padding: '8px 16px',
                            background: filter === cat ? '#1c8ea8' : '#E3E8EF',
                            color: filter === cat ? '#fff' : '#333',
                            border: 'none',
                            borderRadius: 5,
                            cursor: 'pointer'
                        }}
                    >
                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </button>
                ))}
            </div>

            {loading && <p style={{ textAlign: 'center' }}>Loading…</p>}
            {error && <p style={{ textAlign: 'center', color: 'red' }}>{error}</p>}

            <Modal isOpen={editing !== null} onClose={() => setEditing(null)} title={editing ? 'Edit Event' : 'Create New Event'}>
                <EventForm
                    eventTypes={eventTypes}
                    initialData={
                        editing ? events.find(e => e.id === editing) : { event_type_id: eventTypes[0]?.id || '', title: '', description: '', date: new Date().toISOString(), is_active: 1, location: '', fees: 0, is_banner: 0 }
                    }
                    onCancel={() => setEditing(null)}
                    onSubmit={editing ? handleUpdate : handleAdd}
                />
            </Modal>

            <div style={{ marginTop: 20 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 1fr 1fr 1.5fr', alignItems: 'center', padding: '12px 16px', background: '#E3E8EF', borderRadius: 6, fontWeight: 600 }}>
                    <div>Title</div>
                    <div>Date</div>
                    <div>Location</div>
                    <div>Active</div>
                    <div>Banner</div>
                    <div>Actions</div>
                </div>

                {events.map((evt, idx) => {
                    const isLast = idx === events.length - 1;
                    return (
                        <div
                            key={evt.id}
                            ref={isLast ? lastRef : null}
                            style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 1fr 1fr 1.5fr', alignItems: 'center', padding: '12px 16px', background: '#fff', borderRadius: 6, boxShadow: '0 1px 4px rgba(0,0,0,0.05)', marginBottom: 12 }}
                        >
                            <div title={evt.title}>{evt.title}</div>
                            <div>{new Date(evt.date).toLocaleString()}</div>
                            <div>{evt.location}</div>
                            <div>{evt.is_active ? 'Yes' : 'No'}</div>
                            <div>{evt.is_banner ? 'Yes' : 'No'}</div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button style={{ padding: '6px 12px', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13, background: '#1c8ea8', color: '#fff' }} onClick={() => setEditing(evt.id)}>Edit</button>
                                <button style={{ padding: '6px 12px', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13, background: '#fff', color: '#dc3545' }} onClick={() => handleDelete(evt.id)}><DeleteOutline sx={{ color: "#dc3545" }} /></button>

                                {Boolean(evt.is_certificate_required) && Boolean(new Date(evt.date) < new Date()) && 
                                <button style={{ padding: '6px 12px', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13, background: '#fff', color: evt.is_certificate_required == "2" ?   '#16a34a':'#dc3545',whiteSpace: 'nowrap' }} 
                                onClick={() => handleReleaseCertificate(evt)} disabled={evt.is_certificate_required == "2"}>{evt.is_certificate_required == "2" ? 'Certificate Released' : 'Release Certificate'}</button>}
                            </div>
                        </div>
                    );
                })}

                {loading && page > 1 && <p style={{ textAlign: 'center' }}>Loading more…</p>}
                {!hasMore && <p style={{ textAlign: 'center' }}>You've reached the end of the list.</p>}
            </div>
        </div>
    );
}